import { Link } from "react-router-dom";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Pill-shaped CTA link for the landing page.
 *
 * The design specifies exact padding, weight and size per button, which the
 * shadcn <Button> variants (fixed heights, rounded-md) don't express — so this
 * wraps react-router's <Link> with the handoff's pill geometry instead of
 * fighting buttonVariants with a wall of overrides. Same cva pattern as
 * components/ui/button.tsx.
 *
 * Hover darkens ~7% via brightness rather than a hand-picked shade per color,
 * which keeps one rule for all six brand pairs.
 */
const pillVariants = cva(
  "inline-flex items-center justify-center rounded-full text-center transition-[filter,background-color,color] duration-150 ease-out hover:brightness-[0.93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
  {
    variants: {
      variant: {
        /** Hero primary. */
        orange: "bg-eab-orange text-eab-ink text-[17px] font-bold px-[30px] py-[18px]",
        /** Hero secondary, sits on the blue panel. */
        outlineLight:
          "border-[1.5px] border-white/45 text-white text-[17px] font-medium px-[30px] py-[17px] hover:bg-white/10",
        /** Header "Join us". */
        greenDark: "bg-eab-green-dark text-white text-[16px] font-semibold px-6 py-[11px]",
        /** On the green audience card. */
        ink: "bg-eab-ink text-white text-[16px] font-semibold px-7 py-4",
        /** On the blue audience card. */
        white: "bg-white text-eab-blue-dark text-[16px] font-semibold px-7 py-4",
        /** Closing CTA. */
        green: "bg-eab-green text-eab-ink text-[17px] font-bold px-8 py-[19px]",
      },
    },
    defaultVariants: {
      variant: "orange",
    },
  },
);

interface PillLinkProps
  extends React.ComponentPropsWithoutRef<typeof Link>,
    VariantProps<typeof pillVariants> {}

export function PillLink({ className, variant, ...props }: PillLinkProps) {
  return <Link className={cn(pillVariants({ variant }), className)} {...props} />;
}
